import React, { useRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  Sparkles,
  ArrowUp,
  Plus,
  History,
  X,
  MessageSquare,
} from "lucide-react";
import {
  sendMessage,
  Message,
  fetchChatThreads,
  fetchChatThreadDetails,
} from "./actions";
import {
  addMessage,
  updateMessage,
  setMessageStreaming,
  startNewChatSession,
  loadChatSession,
  deleteChatSession,
  updateSessionWithThreadInfo,
  addThreadToList,
} from "./reducer";
import MessageItem from "../../../components/messageItem";
import useDeviceType from "../../../shared/hooks/useDeviceType";
import { DeviceTypes } from "../../../shared/utils/enums";
import { ChatSession } from "../../../interfaces/chat";
import { ChatType } from "../../../shared/utils/enums";
import { ChatThread } from "../../../interfaces/api/chat";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ThreadSkeleton from "./components/ThreadSkeleton";
import MessageSkeleton from "./components/MessageSkeleton";
import { groupThreadsByDate } from "../../../shared/utils/helper";
import Button from "../../../components/button";
import { ButtonVariant } from "../../../shared/utils/enums";

const quickQuestions = [
  "What are the common symptoms of COVID-19?",
  "How can I manage high blood pressure?",
  "What are the early warning signs of diabetes?",
  "How to maintain a healthy heart?",
  "What are the best practices for stress management?",
  "How to improve sleep quality?",
  "What are the benefits of regular exercise?",
  "How to maintain a balanced diet?",
];

const QAScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { threadId: urlThreadId } = useParams();
  const _location = useLocation();
  const {
    currentChatMessages,
    isLoading,
    error: _error,
    chatSessions,
    activeChatId,
    threads,
  } = useSelector((state: RootState) => state.qa);
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const [input, setInput] = useState("");
  const [streamingContent, setStreamingContent] = useState("");
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<
    string | null
  >(null);
  const [isHistorySidebarOpen, setIsHistorySidebarOpen] = useState(false);
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [isLoadingThreadMessages, setIsLoadingThreadMessages] = useState(false);
  const [inputRows, _setInputRows] = useState(1);
  const latestThreadClickRef = useRef<string | null>(null);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamContentRef = useRef("");

  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.mobile;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentChatMessages, streamingContent, loadingThreadId]);

  useEffect(() => {
    if (
      (isMobile || deviceType === DeviceTypes.tablet) &&
      isHistorySidebarOpen
    ) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobile, deviceType, isHistorySidebarOpen]);

  useEffect(() => {
    // Fetch chat threads only once when component mounts
    if (!threadsLoaded && !threadsLoading) {
      setThreadsLoading(true);
      dispatch(fetchChatThreads(ChatType.QNA)).finally(() => {
        setThreadsLoaded(true);
        setThreadsLoading(false);
      });
    }
  }, [dispatch, threadsLoaded, threadsLoading]);

  // Handle initial route and thread loading
  useEffect(() => {
    // Only start a new chat if we're on the base QA route and don't have an active chat
    if (!urlThreadId && !activeChatId) {
      dispatch(startNewChatSession(""));
    }
  }, [urlThreadId, activeChatId, dispatch]);

  // Load thread from URL on initial mount
  useEffect(() => {
    if (urlThreadId && !isNaN(Number(urlThreadId))) {
      handleLoadChat(urlThreadId);
    }
  }, [urlThreadId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (inputRef.current) {
      const textarea = inputRef.current;
      textarea.style.height = "auto";
      const newHeight = Math.min(textarea.scrollHeight, 120); // Max 5 rows
      textarea.style.height = `${newHeight}px`;
    }
  }, [input]);

  const handleSend = async (messageContent: string) => {
    setIsMessageSending(true);

    // Clear input immediately with animation
    setInput("");

    // If no active chat or on welcome screen, start a new chat session
    let sessionId = activeChatId;
    if (!sessionId || currentChatMessages.length === 0) {
      // If we already have an active session but no messages, use it
      if (activeChatId && currentChatMessages.length === 0) {
        sessionId = activeChatId;
      } else {
        const newSessionId = `new_${Date.now()}`;
        dispatch(startNewChatSession(newSessionId));
        sessionId = newSessionId;
        // Navigate to base QA route for new chats
        navigate("/qa", { replace: true });
      }
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageContent,
      isUser: true,
      timestamp: new Date(),
    };

    dispatch(addMessage(userMessage));

    const aiMessageId = (Date.now() + 1).toString();
    const aiMessage: Message = {
      id: aiMessageId,
      content: "",
      isUser: false,
      timestamp: new Date(),
      isStreaming: true,
    };
    dispatch(addMessage(aiMessage));

    setStreamingContent("");
    streamContentRef.current = "";
    setCurrentStreamingMessageId(aiMessageId);

    try {
      // Only send thread_id if it's a server-side thread ID (a number) and not a new session
      const threadId =
        sessionId && !isNaN(Number(sessionId)) && !sessionId.includes("new_")
          ? parseInt(sessionId)
          : undefined;

      await dispatch(
        sendMessage({
          question: userMessage.content,
          threadId,
          onChunk: (chunk, threadId, title) => {
            // If we have thread info, update the session and add to threads list
            if (threadId && title) {
              // Clean up the title - extract meaningful part
              let cleanTitle = title.trim();

              // Handle the specific case where title contains "Patient Clinical Data:" followed by content
              if (cleanTitle.includes("Patient Clinical Data:")) {
                // Extract content after "Patient Clinical Data:" and before any excessive details
                const afterPatientData = cleanTitle.split(
                  "Patient Clinical Data:",
                )[1];
                if (afterPatientData) {
                  // Look for the first meaningful line after "Patient Clinical Data:"
                  const lines = afterPatientData
                    .split("\n")
                    .filter((line: any) => line.trim());
                  if (lines.length > 0) {
                    cleanTitle = lines[0].trim();
                    // If it's still too generic, try the next line
                    if (cleanTitle.length < 10 && lines.length > 1) {
                      cleanTitle = lines[1].trim();
                    }
                  }
                }
              }

              // If title is very long, try to extract the first meaningful line
              if (cleanTitle.length > 80) {
                const lines = cleanTitle
                  .split("\n")
                  .filter((line: any) => line.trim());
                cleanTitle = lines[0] || cleanTitle.substring(0, 50);
              }

              // Remove excessive whitespace and limit length
              cleanTitle = cleanTitle
                .replace(/\s+/g, " ")
                .substring(0, 80)
                .trim();

              // Fallback to a user-friendly title if still empty or just contains generic text
              if (
                !cleanTitle ||
                cleanTitle.length < 5 ||
                cleanTitle.toLowerCase().includes("medical case assessment") ||
                cleanTitle.toLowerCase().includes("patient clinical") ||
                cleanTitle === "Ag......"
              ) {
                cleanTitle = `Medical Chat ${new Date().toLocaleTimeString()}`;
              }

              // Immediately update the session with thread info
              dispatch(
                updateSessionWithThreadInfo({
                  oldId: sessionId!,
                  threadId: threadId.toString(),
                  title: cleanTitle,
                }),
              );

              // Add the new thread to the threads list
              dispatch(
                addThreadToList({
                  id: Number(threadId),
                  title: cleanTitle,
                  created_at: new Date().toISOString(),
                }),
              );

              // Update URL with the new thread ID
              navigate(`/qa/${threadId}`, { replace: true });

              return; // Skip text processing for this chunk
            }

            // Handle regular text chunk
            const cleanChunk = chunk.replace(/\[DONE\]/g, "");
            if (!cleanChunk) return;

            setStreamingContent((prevContent) => {
              const newContent = prevContent + cleanChunk;
              streamContentRef.current = newContent;
              return newContent;
            });

            dispatch(
              updateMessage({
                id: aiMessageId,
                content: streamContentRef.current,
              }),
            );
          },
          onComplete: () => {
            dispatch(
              updateMessage({
                id: aiMessageId,
                content: streamContentRef.current,
              }),
            );
            dispatch(
              setMessageStreaming({ id: aiMessageId, isStreaming: false }),
            );

            setStreamingContent("");
            streamContentRef.current = "";
            setCurrentStreamingMessageId(null);
            setIsMessageSending(false);
          },
          onError: (error) => {
            dispatch(
              updateMessage({
                id: aiMessageId,
                content: `Error: ${error}`,
              }),
            );
            dispatch(
              setMessageStreaming({ id: aiMessageId, isStreaming: false }),
            );

            setStreamingContent("");
            streamContentRef.current = "";
            setCurrentStreamingMessageId(null);
            setIsMessageSending(false);
          },
        }),
      ).unwrap();
    } catch (error) {
      setIsMessageSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading || isMessageSending) return;
    await handleSend(input.trim());
  };

  const handleNewChat = () => {
    dispatch(startNewChatSession(""));
    navigate("/qa"); // Navigate to base QA route for new chats
  };

  const handleLoadChat = async (sessionId: string) => {
    try {
      // Set this as the latest thread click
      latestThreadClickRef.current = sessionId;

      // Update URL with thread ID immediately
      navigate(`/qa/${sessionId}`, { replace: true });

      // Check if this session already exists in chatSessions
      const existingSession = chatSessions.find(
        (session: ChatSession) => session.id === sessionId,
      );

      if (existingSession) {
        // Session already loaded, just switch to it
        dispatch(loadChatSession(sessionId));
        if (isMobile || deviceType === DeviceTypes.tablet) {
          setIsHistorySidebarOpen(false);
        }
        return;
      }

      // Session not loaded, fetch from server
      setLoadingThreadId(sessionId);
      setIsLoadingThreadMessages(true);

      // Set the active chat ID immediately to show loading state
      dispatch(loadChatSession(sessionId));

      // Fetch thread details from server
      const _threadDetails = await dispatch(
        fetchChatThreadDetails(parseInt(sessionId)),
      ).unwrap();

      // Only update URL if this is still the latest thread click
      if (latestThreadClickRef.current === sessionId) {
        navigate(`/qa/${sessionId}`, { replace: true });
      }

      // Close sidebar on mobile/tablet
      if (isMobile || deviceType === DeviceTypes.tablet) {
        setIsHistorySidebarOpen(false);
      }
    } catch (error) {
      console.error("Failed to load chat:", error);
      // Only navigate back if this is still the latest thread click
      if (latestThreadClickRef.current === sessionId) {
        navigate("/qa", { replace: true });
      }
    } finally {
      // Only clear loading state if this is still the latest thread click
      if (latestThreadClickRef.current === sessionId) {
        setLoadingThreadId(null);
        setIsLoadingThreadMessages(false);
      }
    }
  };

  const _handleDeleteChat = (sessionId: string) => {
    dispatch(deleteChatSession(sessionId));
  };

  const ThreadList = ({ isMobile = false }) => {
    const groupedThreads = React.useMemo(
      () => groupThreadsByDate(threads),
      [threads],
    );
    const dateGroups = ["Today", "Yesterday", "Earlier"];

    return (
      <div className={`${isMobile ? "p-4" : "px-4"} space-y-6`}>
        {!isMobile && (
          <div className="px-2 mb-6">
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={handleNewChat}
              className="w-full flex items-center px-4 py-2 rounded-xl bg-primary text-white hover:bg-primary/90 transition-all duration-200 font-medium group"
            >
              <Plus className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90" />
              <span className="w-full text-center">New Chat</span>
            </Button>
          </div>
        )}

        {isMobile && (
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={handleNewChat}
            className="w-full flex items-center px-4 py-2.5 rounded-xl bg-primary text-white hover:bg-primary/90 transition-all duration-200 font-medium group mb-4"
          >
            <Plus className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90" />
            <span className="w-full text-center">New Chat</span>
          </Button>
        )}

        {threadsLoading ? (
          <div className="space-y-6">
            {dateGroups.map((group) => (
              <div key={group} className="space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-[#1f1f1f] rounded w-1/4 animate-pulse px-2" />
                <div className="space-y-1">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <ThreadSkeleton key={i} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          dateGroups.map((group) => {
            const groupThreads = groupedThreads[group] || [];
            if (groupThreads.length === 0) return null;

            return (
              <div key={group} className="space-y-2">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 px-2">
                  {group}
                </h3>
                <div className="space-y-1">
                  {groupThreads.map((thread: ChatThread) => (
                    <button
                      key={thread.id}
                      onClick={() => handleLoadChat(thread.id.toString())}
                      disabled={isLoadingThreadMessages}
                      className={`relative w-full text-left px-3 py-2 rounded-lg transition-all duration-200 group flex items-center gap-2.5 ${
                        activeChatId === thread.id.toString() ||
                        loadingThreadId === thread.id.toString()
                          ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light border border-primary/20"
                          : "hover:bg-gray-100 dark:hover:bg-[#2a2a2a] text-gray-800 dark:text-gray-300"
                      } ${isLoadingThreadMessages ? "cursor-not-allowed" : ""}`}
                    >
                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                        {loadingThreadId === thread.id.toString() ? (
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <MessageSquare
                            className={`w-4 h-4 transition-transform duration-200 group-hover:scale-110 ${
                              activeChatId === thread.id.toString()
                                ? "text-primary dark:text-primary-light"
                                : ""
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <p
                          className={`font-medium text-sm truncate ${
                            activeChatId === thread.id.toString()
                              ? "text-primary dark:text-primary-light font-semibold"
                              : ""
                          }`}
                        >
                          {thread.title}
                        </p>
                      </div>
                      {activeChatId === thread.id.toString() && (
                        <div className="w-2 h-2 bg-primary dark:bg-primary-light rounded-full flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  // FIXED: Better condition to show welcome screen vs loading state
  const shouldShowWelcomeScreen = () => {
    // Show welcome screen only if:
    // 1. No messages AND
    // 2. Not loading thread messages AND
    // 3. Either no active chat or active chat starts with "new_"
    return (
      currentChatMessages.length === 0 &&
      !isLoadingThreadMessages &&
      (!activeChatId || activeChatId.startsWith("new_"))
    );
  };

  const shouldShowLoadingSkeleton = () => {
    // Show loading skeleton if:
    // 1. No messages AND
    // 2. We are loading thread messages AND
    // 3. We have an active chat that doesn't start with "new_"
    return (
      currentChatMessages.length === 0 &&
      isLoadingThreadMessages &&
      activeChatId &&
      !activeChatId.startsWith("new_")
    );
  };

  // Handle textarea height adjustment
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);

    // Reset height to auto to get the correct scrollHeight
    e.target.style.height = "auto";

    // Calculate new height (min 1 row, max 5 rows)
    const newHeight = Math.min(Math.max(e.target.scrollHeight, 44), 120);
    e.target.style.height = `${newHeight}px`;
  };

  return (
    <div className="flex h-full">
      {/* Desktop Chat History Sidebar */}
      {!isMobile && deviceType !== DeviceTypes.tablet && (
        <div className="w-72 flex-shrink-0 bg-white dark:bg-[#171717] border-r border-gray-200 dark:border-gray-800 overflow-y-auto pt-6 pb-6">
          <ThreadList />
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-gray-50 dark:bg-[#171717]">
        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-6">
          {shouldShowWelcomeScreen() ? (
            <div
              className={`h-full flex flex-col items-center ${isMobile ? "pt-[100px]" : "justify-center"} text-center px-4 overflow-y-auto`}
            >
              <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-500 to-primary/60 dark:from-primary-light dark:via-purple-400 dark:to-primary mb-4">
                Welcome, {currentUser?.first_name || "Doctor"}!
              </h2>
              {!isMobile && (
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-12 w-full md:w-[60%] leading-relaxed">
                  I'm here to help you with any medical questions or concerns
                  you may have. Feel free to ask anything or try one of these
                  common questions.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-3xl">
                {quickQuestions.map((question, index) => (
                  <Button
                    variant={ButtonVariant.SECONDARY}
                    key={index}
                    onClick={() => handleSend(question)}
                    disabled={isMessageSending}
                    className="group px-5 py-2.5 rounded-xl bg-white dark:bg-[#1f1f1f] text-gray-800 dark:text-gray-300 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 border border-gray-200 dark:border-gray-800 hover:border-primary/30 dark:hover:border-primary/40 hover:text-primary text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-sm font-medium">{question}</span>
                  </Button>
                ))}
              </div>
            </div>
          ) : shouldShowLoadingSkeleton() ? (
            <div className="space-y-6">
              {/* User message skeleton */}
              <MessageSkeleton isUser={true} />
              {/* AI message skeleton */}
              <MessageSkeleton isUser={false} />
              {/* Another AI message skeleton */}
              <MessageSkeleton isUser={false} />
              <MessageSkeleton isUser={true} />
              <MessageSkeleton isUser={false} />
            </div>
          ) : (
            <div className="space-y-6">
              {currentChatMessages.map((msg: Message) => (
                <MessageItem
                  key={msg.id}
                  message={msg}
                  currentStreamingMessageId={currentStreamingMessageId}
                  streamingContent={streamingContent}
                />
              ))}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4">
          <form onSubmit={handleSubmit} className="relative max-w-4xl mx-auto">
            {/* Input container */}
            <div className="flex items-start bg-white dark:bg-[#1f1f1f] rounded-xl p-2">
              <div className="flex items-center pl-4 pr-3 pt-3">
                <Sparkles className="w-5 h-5 text-primary/60" />
              </div>

              <textarea
                ref={inputRef}
                value={input}
                onChange={handleTextareaChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="Ask me anything..."
                disabled={isMessageSending}
                rows={inputRows}
                className={`flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-gray-300 placeholder-gray-400 text-base py-3 pr-4 focus:ring-0 transition-all duration-300 resize-none ${
                  isMessageSending ? "opacity-50" : ""
                }`}
                style={{ minHeight: "44px", maxHeight: "120px" }}
              />

              {/* Desktop button - only show on desktop */}
              {!isMobile && deviceType !== DeviceTypes.tablet && (
                <Button
                  variant={ButtonVariant.PRIMARY}
                  type="submit"
                  disabled={!input.trim() || isLoading || isMessageSending}
                  className={`self-end flex items-center justify-center bg-primary hover:bg-primary/90 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-xl px-6 py-3 transition-all duration-300 font-medium disabled:opacity-50 ${
                    isMessageSending ? "animate-pulse" : ""
                  }`}
                >
                  {isMessageSending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <ArrowUp className="w-4 h-4 mr-2" />
                  )}
                  {isMessageSending ? "Sending..." : "Send"}
                </Button>
              )}
            </div>

            {/* Mobile and Tablet buttons - below textarea */}
            {(isMobile || deviceType === DeviceTypes.tablet) && (
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setIsHistorySidebarOpen(true)}
                  className="w-[20%] flex items-center justify-center bg-gray-100 dark:bg-[#1f1f1f] text-gray-600 dark:text-gray-300 rounded-xl p-3 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition-colors duration-200"
                >
                  <History className="w-5 h-5" />
                </button>

                <Button
                  variant={ButtonVariant.PRIMARY}
                  type="submit"
                  disabled={!input.trim() || isLoading || isMessageSending}
                  className={`w-[80%] flex items-center justify-center bg-primary hover:bg-primary/90 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-xl px-4 py-3 transition-all duration-300 font-medium disabled:opacity-50 ${
                    isMessageSending ? "animate-pulse" : ""
                  }`}
                >
                  {isMessageSending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <ArrowUp className="w-4 h-4 mr-2" />
                  )}
                  {isMessageSending ? "Sending..." : "Send"}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Mobile and Tablet Chat History Sidebar */}
      {(isMobile || deviceType === DeviceTypes.tablet) && (
        <div
          className={`fixed top-0 right-0 h-full w-full bg-white dark:bg-[#171717] z-50 transform transition-transform duration-300 ease-in-out ${
            isHistorySidebarOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-lg font-semibold text-primary dark:text-primary-light">
              Chat History
            </h2>
            <button
              onClick={() => setIsHistorySidebarOpen(false)}
              className="p-2 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors duration-200"
            >
              <X size={24} />
            </button>
          </div>
          <ThreadList isMobile={true} />
        </div>
      )}
    </div>
  );
};

export default QAScreen;
